/**
 * @typedef {Object} ExtendedOfferVariant
 * @property {string} title
 * @property {number} id
 * @property {string} image_url
 * @property {string} price
 * @property {string} option1
 * @property {string} option2
 * @property {string} option3
 */

if (!customElements.get("extended-offer-modal")) {
  customElements.define(
    "extended-offer-modal",

    class ExtendedOfferModal extends HTMLElement {
      constructor() {
        super();
        // console.log("extended offer modal constructor");

        this.modalOpen = false;
        this.shopDomain = this.dataset.shopDomain;
        /**
         * @type { HTMLScriptElement | null }
         */
        const productJson = this.querySelector("script");
        if (!productJson || !productJson.textContent) {
          console.log("Cannot find mandatory product element...");
          return;
        }
        this.product = JSON.parse(productJson.textContent);
        /** @type {Array<ExtendedOfferVariant>} */
        this.soldoutVariants = [];
        this.buildSoldOutVariantsArray();
        /** @type {Array<string>} */
        this.optionNames = [];
        this.buildOptionNames();
        /** @type {Array<HTMLSelectElement>} */
        this.optionSelects = [];
        /** @type {Array<Array<string>>} */
        this.optionValues = [[], [], []];
        this.getOptionValues();
        /** @type {HTMLElement} */
        this.carret = document.createElement("svg");
        this.carret.innerHTML = `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor">
  </svg>`;
        this.addToMailingList = false;
      }

      connectedCallback() {
        // console.log("extended-offer connected callback");

        // a11y: setup focus trap instance and previous focus tracking
        this._previouslyFocused = null;
        this._focusTrap = (typeof focusTrap !== 'undefined')
          ? focusTrap.createFocusTrap(this, {
              initialFocus: () => this.querySelector('#remind-email-auth-input') || this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || this,
              escapeDeactivates: true,
              allowOutsideClick: true,
              onDeactivate: () => {
                if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
                  this._previouslyFocused.focus();
                }
              }
            })
          : null;

        this.companyId = this.dataset.companyId;
        this.shopDomain = this.dataset.shopDomain;
        const scriptElement = this.querySelector("script");
        if (!scriptElement) {
          console.error("Cannot find script element!");
          return;
        }
        /**
         * @type {Product | undefined}
         */
        this.product = JSON.parse(scriptElement.textContent || "{}");
        if (!this.product) {
          console.error("Cannot find product!");
          return;
        }

        this.section = this.closest("section");
        if (!this.section) {
          console.log("Cannot find closest section...");
          return;
        }

        this.extendedOfferButton = this.section.querySelector(
          '[id^="extended-offer-button"]'
        );
        if (!this.extendedOfferButton) {
          console.log("Cannot find extended-offer button...");
          return;
        }

        this.closeX = this.querySelector(".close__btn");
        if (!this.closeX) {
          console.log("Cannot find closeX...");
          return;
        }

        /**
         * @type {HTMLButtonElement | null}
         */
        this.submitButton = this.querySelector("#remind-email-auth-button");
        /**
         * @type {HTMLInputElement | null}
         */
        this.subscribeCheckbox = this.querySelector("#subscribe-to-mail-list");
        if (!this.subscribeCheckbox) {
          console.error("Cannot find subscribe checkbox!");
          return;
        }

        if (this.subscribeCheckbox) {
          this.subscribeCheckbox.addEventListener("change", () => {
            if (!this.subscribeCheckbox) return;
            this.addToMailingList = this.subscribeCheckbox.checked
              ? true
              : false;
          });
        }

        if (this.submitButton) {
          this.submitButton.addEventListener("click", (e) => {
            e.preventDefault();
            if (typeof this.addToMailingList !== "undefined") {
              this.addToWatchlist(this.addToMailingList);
            }
          });
        }

        if (this.extendedOfferButton) {
          this.extendedOfferButton.addEventListener("click", () => {
            this.openModal();
          });
        }

        // if (this.extendedOfferInfo) {
        //   this.extendedOfferInfo.addEventListener("click", () => {
        //     this.openModal();
        //   });
        // }

        this.closeX.addEventListener("click", () => {
          this.closeModal();
        });

        // a11y: close on Escape
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            this.closeModal();
          }
        });

        window.addEventListener("click", (e) => {
          if (e.target == this) {
            this.closeModal();
          }
        });
      }

      openModal() {
        if (!this.companyId) {
          console.log("Cannot find companyId");
          return;
        }

        this.setVariantPreset();
        this.classList.add("is-visible");

        // a11y: activate focus trap and remember previously focused element
        this._previouslyFocused = document.activeElement;
        if (this._focusTrap) this._focusTrap.activate();

        /** @type {HTMLInputElement | null} */
        const remindEmailAuthInput = this.querySelector(
          "#remind-email-auth-input"
        );
        /** @type {HTMLElement | null} */
        const remindEmailLoading = this.querySelector("#remind-email-loading");
        /** @type {HTMLElement | null} */
        const remindEmailSuccess = this.querySelector("#remind-email-success");
        /** @type {HTMLElement | null} */
        const remindEmailError = this.querySelector("#remind-email-error");
        /** @type {HTMLButtonElement | null} */
        const remindEmailAuthButton = this.querySelector(
          "#remind-email-auth-button"
        );

        if (
          !remindEmailAuthInput ||
          !remindEmailLoading ||
          !remindEmailSuccess ||
          !remindEmailError ||
          !remindEmailAuthButton
        ) {
          console.log("Cannot find neccessary elements to open modal...");
          return;
        }

        remindEmailAuthInput.style.display = "block";
        remindEmailLoading.style.display = "none";
        remindEmailSuccess.style.display = "none";
        remindEmailError.style.display = "none";
        remindEmailAuthButton.disabled = false;
        // a11y: reset form field state
        remindEmailAuthInput.setAttribute('aria-invalid', 'false');
        remindEmailAuthInput.removeAttribute('aria-describedby');

        this.modalOpen = true;
      }

      closeModal() {
        this.classList.remove("is-visible");
        this.modalOpen = false;
        // a11y: deactivate focus trap
        if (this._focusTrap) this._focusTrap.deactivate();
      }

      setVariantPreset() {
        // console.log("setting variant preset...");
        const variantInject = this.querySelector("#variant-inject");

        if (!variantInject) {
          console.log("variantInject not found...");
          return;
        }

        /** @type {HTMLInputElement | null} */
        const currentVariantElement =
          document.querySelector('input[name="id"]');
        if (!currentVariantElement) {
          console.log("Cannot find current-variant-element");
          return;
        }

        /**
         * @type {ExtendedOfferVariant | undefined}
         */
        const currentVariant = this.soldoutVariants.find((variant) => {
          return variant.id.toString() == currentVariantElement.value;
        });
        if (!currentVariant) {
          console.error("Cannot find current variant in soldout variants!");
          return;
        }

        // delete all child elements of variantInject
        while (variantInject.firstChild) {
          variantInject.removeChild(variantInject.firstChild);
        }

        if (!this.optionNames) return;
        for (let i = 0; i < this.optionNames.length; i++) {
          let optionName = this.optionNames[i];
          if (!currentVariant) continue;
          let optionValue = /** @type {String} */ (
            currentVariant[`option${(i + 1).toString()}`]
          );
          let optionDiv = document.createElement("div");

          let optionPTagKey = document.createElement("p");
          optionPTagKey.setAttribute("class", "option-key");
          optionPTagKey.innerHTML = `${optionName}:`;

          let optionPTagValue = document.createElement("p");
          optionPTagValue.setAttribute("id", `option-${i}`);
          optionPTagValue.setAttribute("class", "option-value");
          optionPTagValue.innerHTML = `${optionValue}`;

          optionDiv.appendChild(optionPTagKey);
          optionDiv.appendChild(optionPTagValue);
          variantInject.appendChild(optionDiv);
        }
      }

      buildSoldOutVariantsArray() {
        if (!this.product) {
          console.error("Cannot find product!");
          return;
        }
        for (const variant of this.product.variants) {
          if (!variant.available) {
            /**
             * @type {ExtendedOfferVariant}
             */
            let soldOutVariant = {
              title: variant.title,
              id: variant.id,
              image_url: variant.image_url
                ? variant.image_url
                : this.product.featured_image,
              price: knocking.formatToDollarCurrencyString(
                variant.price.toString()
              ),
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
            };
            this.soldoutVariants.push(soldOutVariant);
          }
        }
      }

      buildOptionNames() {
        if (!this.product) {
          console.error("Cannot find product!");
          return;
        }
        for (const option of this.product.options) {
          this.optionNames.push(option);
        }
      }

      getOptionValues() {
        for (let i = 0; i < this.optionNames.length; i++) {
          for (let j = 0; j < this.soldoutVariants.length; j++) {
            let variant = this.soldoutVariants[j];
            if (!this.optionValues[i].includes(variant[`option${i + 1}`])) {
              this.optionValues[i].push(variant[`option${i + 1}`]);
            }
          }
        }
      }

      buildOptionSelects() {
        for (let i = 0; i < this.optionNames.length; i++) {
          const element = this.optionNames[i];

          let select = document.createElement("select");
          select.setAttribute(
            "class",
            "variant__input-sold-out select__select"
          );
          select.setAttribute("name", "variant_dropdown_select-sold-out");
          select.setAttribute("id", `option-${i}`);
          select.setAttribute("data-index", `option${i}-sold-out`);
          select.setAttribute("required", "true");

          let firstHiddenOption = document.createElement("option");
          firstHiddenOption.setAttribute("hidden", "true");
          firstHiddenOption.textContent = `Select ${element.toUpperCase()}`;

          select.appendChild(firstHiddenOption);

          if (this.optionSelects) {
            this.optionSelects.push(select);
          }
        }
      }

      appendSelectsToForm() {
        if (!this.product) {
          console.error("Cannot find product!");
          return;
        }
        if (
          this.product.options.length === 1 &&
          this.product.variants[0].title === "Default Title"
        ) {
          return;
        }

        for (let i = 0; i < this.optionValues.length; i++) {
          for (let j = 0; j < this.optionValues[i].length; j++) {
            const value = this.optionValues[i][j];
            let option = document.createElement("option");
            option.setAttribute("value", value);
            option.setAttribute("name", `${this.optionNames[i]}-sold-out`);
            option.textContent = value;

            this.optionSelects[i].appendChild(option);
          }
        }

        /** @type {HTMLElement | null} */
        let selectInject = this.querySelector("#select-inject");
        if (!selectInject) {
          console.log("Cannot find FormSelectInject...");
          return;
        }

        for (let i = 0; i < this.optionSelects.length; i++) {
          let select = this.optionSelects[i];
          let wrapper = document.createElement("div");
          wrapper.setAttribute("class", "variant-input-wrap select");

          let label = document.createElement("label");
          label.setAttribute("class", "variant__label form__label");
          label.setAttribute(
            "for",
            `SingleOptionSelectorExtendedOffer-product-template-option-${i}`
          );
          label.textContent = this.optionNames[i];

          selectInject.appendChild(label);
          wrapper.appendChild(select);
          wrapper.appendChild(this.carret);

          selectInject.appendChild(wrapper);
        }
      }

      /**
       * @param {Boolean} addToMailingList
       * @returns {Promise<void>}
       */
      async addToWatchlist(addToMailingList) {
        if (!this.product) {
          console.error("Cannot find product!");
          return;
        }

        /**
         * @type {HTMLFormElement | null}
         */
        const extendedOfferForm = this.querySelector(".extended_offer__form");
        if (!extendedOfferForm) {
          console.log("Cannot find extended-offer form...");
          return;
        }
        const isValid = extendedOfferForm.checkValidity();

        /**
         * @type {HTMLElement | null}
         */
        const loading = this.querySelector("#remind-email-loading"); // has to be outside of the callback for scope
        /**
         * @type {HTMLElement | null}
         */
        const success = this.querySelector("#remind-email-success");
        /**
         * @type {HTMLElement | null}
         */
        const error = this.querySelector("#remind-email-error");

        if (!error || !loading || !success) {
          console.log("Missing mandatory elements...");
          return;
        }

        if (!isValid) {
          error.textContent = "Please enter a valid email address.";
          loading.style.display = "none";
          error.style.display = "block";
          // a11y: flag invalid input and describe error
          const emailInput = this.querySelector('#remind-email-auth-input');
          if (emailInput) {
            emailInput.setAttribute('aria-invalid', 'true');
            emailInput.setAttribute('aria-describedby', 'remind-email-error');
          }
          return;
        }

        let option1Element = this.querySelector("#option-0");
        let option2Element = this.querySelector("#option-1");
        let option3Element = this.querySelector("#option-2");

        // non-existent options default to empty strings
        const option1 =
          option1Element == undefined ? null : option1Element.textContent;
        const option2 =
          option2Element == undefined ? null : option2Element.textContent;
        const option3 =
          option3Element == undefined ? null : option3Element.textContent;

        let variant = null;

        if (this.soldoutVariants.length == 1) {
          variant = this.soldoutVariants[0];
        } else {
          // search correct variant
          for (let i = 0; i < this.soldoutVariants.length; i++) {
            let v = this.soldoutVariants[i];

            // Variant options need to correspond to selected options
            if (
              option1 == v.option1 &&
              option2 == v.option2 &&
              option3 == v.option3
            ) {
              // Found the correct variant!
              variant = v;
              break;
            }
          }
        }

        if (variant == null) {
          console.log("variant not found...");
          return;
        }

        // hide email input
        /**
         * @type {HTMLInputElement | null}
         */
        const remindEmailAuthInput = this.querySelector(
          "#remind-email-auth-input"
        );
        if (!remindEmailAuthInput) {
          console.log("Cannot find remind email auth input...");
          return;
        }
        remindEmailAuthInput.style.display = "none";

        let email_address = remindEmailAuthInput.value;
        let mailinglist_optin = addToMailingList ? 1 : 0;
        let canonical_url = `https://${this.shopDomain}/products/${this.product.handle}`;

        // success callback
        let callbackHandler = function (result) {
          error.style.display = "none";
          loading.style.display = "none";
          success.style.display = "block";
          // a11y: clear invalid state on success
          const emailInput = /** @type {HTMLInputElement|null} */ (document.querySelector('#remind-email-auth-input'));
          if (emailInput) {
            emailInput.setAttribute('aria-invalid', 'false');
            emailInput.setAttribute('aria-describedby', 'remind-email-success');
          }
        };

        // error callback
        /**
         * @param {any} err
         */
        let errorHandler = function (err) {
          if (err.message) {
            error.textContent = err.message;
          }
          loading.style.display = "none";
          error.style.display = "block";
          // a11y: flag invalid input and describe error
          const emailInput = /** @type {HTMLInputElement|null} */ (document.querySelector('#remind-email-auth-input'));
          if (emailInput) {
            emailInput.setAttribute('aria-invalid', 'true');
            emailInput.setAttribute('aria-describedby', 'remind-email-error');
          }
        };

        // show loading animation
        /**
         * @type {HTMLElement | null}
         */
        const remindEmailLoading = this.querySelector("#remind-email-loading");
        if (!remindEmailLoading) {
          console.log("Cannot find loading element...");
          return;
        }
        remindEmailLoading.style.display = "block";

        // Disable submit button
        /**
         * @type {HTMLButtonElement | null}
         */
        const remindEmailAuthButton = this.querySelector(
          "#remind-email-auth-button"
        );
        if (!remindEmailAuthButton) {
          console.log("Cannot find email auth button...");
          return;
        }
        remindEmailAuthButton.disabled = true;

        const payload = `{
          "data": {
            "type": "back-in-stock-subscription",
            "attributes": {
              "profile": {
                "data": {
                  "type": "profile",
                  "attributes": {
                    "email": "${email_address}"
                  }
                }
              },
              "channels": [
                "EMAIL"
              ]
            },
            "relationships": {
              "variant": {
                "data": {
                  "type": "catalog-variant",
                  "id": "$shopify:::$default:::${variant.id}"

                }
              }
            }
          }
        }`;

        // console.log("payload: ", payload);

        if (!this.companyId) {
          console.log("Missing company id, cannot post to Klaviyo...");
          return;
        }

        try {
          const res = await fetch(
            `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${this.companyId}`,
            {
              method: "POST",
              headers: {
                accept: "application/vnd.api+json",
                revision: "2024-10-15",
                "content-type": "application/vnd.api+json",
              },
              body: payload,
            }
          );

          // console.log("res: ", res);
          // console.log("status: ", res.status);
          if (res.status < 300 && res.status >= 200) {
            callbackHandler();
          } else {
            throw new Error("Klaviyo status not in the 200 range.");
          }
        } catch (err) {
          console.log("Error in BIS: ", err);
          errorHandler(err);
        }
      }
    }
  );
} else {
  console.log("extended-offer-modal already defined...");
}
